import { Select, SelectProvider } from '@ariakit/react'
import { useEffect, useMemo, useState } from 'react'
import { generatePath, useNavigate, useOutletContext } from 'react-router'
import { ExpandImageButton } from '@/components/commons/expand-image-button'
import { ImageCard } from '@/components/commons/image-card'
import { PageFooter } from '@/components/commons/page-footer'
import { PageHero } from '@/components/commons/page-hero'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { ButtonLinkLegacy } from '@/components/ui/button-link-legacy'
import { Container } from '@/components/ui/container'
import {
	CheckIcon,
	ChevronDownIcon,
	DownloadCloud02Icon,
	ImageXIcon
} from '@/components/ui/icons'
import { SelectItem, SelectPopover } from '@/components/ui/select-atoms-legacy'
import { Skeleton } from '@/components/ui/skeleton'
import { Spinner } from '@/components/ui/spinner'
import { GalleryCurationModal } from '@/features/photoset/components/gallery-photoset-modal/gallery-curation-modal/gallery-curation-modal'
import { PhotosetStatusMessage } from '@/features/photoset/components/photoset-status-message/photoset-status-message'
import {
	PhotosetStepBodyContainer,
	PhotosetStepWrapper
} from '@/features/photoset/components/photoset-step-atoms'
import { SubHeaderBanner } from '@/features/photoset/components/sub-header-banner'
import { useGetPhotoset } from '@/features/photoset/queries/use-get-photoset'
import { useGetPhotosetImagesList } from '@/features/photoset/queries/use-get-photoset-images-list'
import { useUpdateStepToCompleted } from '@/features/photoset/queries/use-update-step-to-completed'
import { usePhotosetStore } from '@/features/photoset/store/store.ts'
import type { Image, PhotosetURLParams } from '@/features/photoset/types.ts'
import { handleImagesRangeSelect } from '@/features/photoset/utils.ts'
import { errorHandler } from '@/helpers/utils'
import { QUERY_KEYS, queryClient } from '@/query-client'
import { routes } from '@/routes'
import { type TKey, useTranslation } from '@/translations/i18n'

type ImageFilters = 'all' | 'selected' | 'unselected'

const NoPhotosView = () => {
	const { t } = useTranslation()
	return (
		<Container className='flex flex-col items-center justify-center'>
			<PhotosetStatusMessage gap={3}>
				<ImageXIcon size={48} />
				<h1 className='font-bold text-display-xs text-neutral-900'>
					{t('photoset:curation_v1.title_no_photo')}
				</h1>
				<p className='text-neutral-900 text-xl'>
					{t('photoset:curation_v1.paragraph_no_photo')}
				</p>
			</PhotosetStatusMessage>
		</Container>
	)
}

const ThumbnailGeneratingView = ({
	isFetching,
	zipUrl
}: {
	isFetching: boolean
	zipUrl?: string | null
}) => {
	const { t } = useTranslation()
	return (
		<Container className='flex flex-col items-center justify-center'>
			<PhotosetStatusMessage gap={3}>
				<Spinner className='fill-neutral-black' size={32} />
				<h1 className='font-bold text-display-xs text-neutral-900'>
					{t('photoset:show.thumb_gen_title')}
				</h1>
				<p className='text-neutral-900 text-xl'>
					{t('photoset:show.thumb_gen_subtitle')}
				</p>
				{zipUrl && (
					<ButtonLinkLegacy
						disabled={isFetching}
						size='sm'
						to={zipUrl}
						variant='secondary'
					>
						{t('photoset:imagen_ai.completed.download_all_set')}
						<DownloadCloud02Icon />
					</ButtonLinkLegacy>
				)}
			</PhotosetStatusMessage>
		</Container>
	)
}

const CurationImageFilterHeader = ({
	images,
	imageFilters,
	setImageFilters,
	remappedImages,
	selectedImages,
	onSelectAll,
	unselectAll
}: {
	images: Image[]
	imageFilters: ImageFilters
	setImageFilters: (value: ImageFilters) => void
	remappedImages: Image[]
	selectedImages: number[]
	onSelectAll: () => void
	unselectAll: () => void
}) => {
	const { t } = useTranslation()

	return (
		<div className='flex w-full items-center justify-between'>
			<div className='flex items-center gap-4'>
				<h4>
					{images.length} {t('common:images')}
				</h4>

				{images.length > 0 && (
					<SelectProvider
						setValue={(value: ImageFilters) =>
							setImageFilters(value)
						}
						value={imageFilters}
					>
						<Select
							render={
								<ButtonLegacy
									className='justify-between rounded-none border-neutral-300 border-l'
									size='sm'
									style={{ minWidth: 160 }}
									type='button'
									variant='tertiary'
								>
									<div className='flex items-center gap-1'>
										<span>
											{t(
												`photoset:curation_v1.filters.${imageFilters}`
											)}
										</span>
										<span>
											{imageFilters !== 'all' &&
												`(${remappedImages.length})`}
										</span>
									</div>
									<ChevronDownIcon />
								</ButtonLegacy>
							}
						/>

						<SelectPopover gutter={4} sameWidth>
							{['selected', 'unselected', 'all'].map((option) => (
								<SelectItem key={option} value={option}>
									<span>
										{t(
											`photoset:curation_v1.filters.${option}` as TKey
										)}
									</span>
									{option === imageFilters && (
										<CheckIcon size={20} />
									)}
								</SelectItem>
							))}
						</SelectPopover>
					</SelectProvider>
				)}
			</div>

			{images.length > 0 && selectedImages.length === 0 ? (
				<ButtonLegacy
					onClick={onSelectAll}
					size={'sm'}
					type='button'
					variant={'tertiary'}
				>
					{t('common:select_all')}
				</ButtonLegacy>
			) : (
				selectedImages.length > 0 && (
					<div className='flex items-center'>
						<p>
							{t('common:x_images_selected', {
								count: selectedImages.length
							})}
						</p>
						{selectedImages.length > 0 && (
							<ButtonLegacy
								onClick={unselectAll}
								size={'sm'}
								type='button'
								variant={'tertiary'}
							>
								{t('common:unselect_all')}
							</ButtonLegacy>
						)}
					</div>
				)
			)}
		</div>
	)
}

export const PhotosetCurationStepV1 = () => {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const { urlParams } = useOutletContext<{ urlParams: PhotosetURLParams }>()
	const selectImages = usePhotosetStore((state) => state.selectImages)
	const unselectAll = usePhotosetStore((state) => state.unselectAll)
	const unselectImages = usePhotosetStore((state) => state.unselectImages)
	const selectedImages = usePhotosetStore((state) => state.selectedImages)
	const [currentDetailImageIndex, setCurrentDetailImageIndex] = useState<
		number | null
	>(null)
	const [imageFilters, setImageFilters] = useState<ImageFilters>('all')
	//queries
	const { data: photoset, isFetching: isPhotosetFetching } = useGetPhotoset(
		{ id: urlParams.id },
		{ enabled: false }
	)
	const {
		data: images,
		isFetching: isImageFetching,
		isError
	} = useGetPhotosetImagesList({ urlParams: { photosetId: urlParams.id } })
	//state
	const [lastSelectedImageId, setLastSelectedImageId] = useState<
		number | null
	>(null)
	const [curationTouched, setCurationTouched] = useState(false)
	//checks
	const isFetching = isPhotosetFetching || isImageFetching
	const showErrorBanner = useMemo(
		() => selectedImages.length < 1,
		[selectedImages]
	)
	//mutations
	const updateStepToCompleted = useUpdateStepToCompleted({
		onError: (error) => errorHandler(error),
		onSuccess: () => {
			navigate(
				generatePath(routes.PHOTOSET_IMAGEN_AI_STEP_V1.path, {
					id: String(urlParams.id)
				})
			)
			queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PHOTOSET] })
			queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.IMAGES] })
		}
	})

	const onSendToImagenAi = () => {
		setCurationTouched(true)
		if (!showErrorBanner) {
			updateStepToCompleted.mutate({
				photosetId: urlParams.id,
				imagesIds: selectedImages
			})
		}
	}

	const onSelectAll = () => {
		if (images?.length) {
			for (const image of images) {
				selectImages([image.id])
			}
		}
	}

	const onExpandGallerys = (imageId: Image['id']) => {
		if (!images || images.length < 0) {
			return
		}
		setCurrentDetailImageIndex(
			images.findIndex((imageListItem) => imageListItem.id === imageId)
		)
	}

	//If curation or allocation process is already completed navigate to imagen-ai route
	useEffect(() => {
		if (photoset) {
			if (photoset.curation_completed_at) {
				navigate(
					generatePath(routes.PHOTOSET_IMAGEN_AI_STEP_V1.path, {
						id: String(urlParams.id)
					})
				)
			} else if (photoset.allocation_completed_at) {
				navigate(
					generatePath(routes.PHOTOSET_INITIAL_QA_V1.path, {
						id: String(urlParams.id)
					})
				)
			}
		}
	}, [photoset, navigate, urlParams.id])

	// Sort images by file name
	const remappedImages = useMemo(
		() =>
			images && images?.length > 0
				? images.filter((image) => {
						if (imageFilters === 'all') {
							return image
						}
						if (imageFilters === 'selected') {
							return selectedImages.includes(image.id)
						}
						if (imageFilters === 'unselected') {
							return !selectedImages.includes(image.id)
						}

						return image
					})
				: [],
		[images, imageFilters, selectedImages]
	)

	if (!(isFetching || isError) && images?.length === 0) {
		return <NoPhotosView />
	}

	if (!(isFetching || photoset?.thumbnail_generated_at)) {
		return (
			<ThumbnailGeneratingView
				isFetching={isFetching}
				zipUrl={photoset?.zip_url}
			/>
		)
	}

	return (
		<div className='grid grid-rows-[auto_1fr] overflow-auto'>
			<PageHero
				endSlot={
					photoset?.zip_url && (
						<ButtonLinkLegacy
							disabled={isFetching}
							size='sm'
							to={photoset.zip_url}
							variant='secondary'
						>
							{t('photoset:imagen_ai.completed.download_all_set')}
							<DownloadCloud02Icon />
						</ButtonLinkLegacy>
					)
				}
				subtitle={t('photoset:curation.subtitle_v1')}
				title={t('photoset:curation.title')}
			/>
			{showErrorBanner && curationTouched && (
				<SubHeaderBanner
					subtitle={t(
						'photoset:curation.selection_error_subtitle_v1',
						{ selected: selectedImages.length }
					)}
					title={t('photoset:curation.selection_error_title_v1')}
					type={'danger'}
				/>
			)}
			<PhotosetStepWrapper>
				<PhotosetStepBodyContainer>
					<div className='relative z-[1] flex w-full items-center justify-between gap-1 px-6 py-4'>
						{isImageFetching ? (
							<Skeleton height={28} width={80} />
						) : (
							<CurationImageFilterHeader
								imageFilters={imageFilters}
								images={images || []}
								onSelectAll={onSelectAll}
								remappedImages={remappedImages}
								selectedImages={selectedImages}
								setImageFilters={setImageFilters}
								unselectAll={unselectAll}
							/>
						)}
					</div>

					<div className='grid w-full grid-cols-4'>
						{isFetching &&
							[...new Array(20).keys()].map((e) => (
								<ImageCard asSkeleton key={`${e}-id`} />
							))}
						{!isFetching &&
							remappedImages.map((image, index, array) => {
								const isSelected = selectedImages.some(
									(selectedImageId) =>
										selectedImageId === image.id
								)
								return (
									<ImageCard
										endSlot={
											<ExpandImageButton
												onClick={() =>
													onExpandGallerys(image.id)
												}
											/>
										}
										image={image}
										isSelected={isSelected}
										key={image.id}
										onSelect={(isShiftPressed) => {
											handleImagesRangeSelect({
												array,
												image,
												index,
												isSelected,
												isShiftPressed,
												lastSelectedImageId,
												selectImages,
												setLastSelectedImageId,
												unselectImages
											})
										}}
										thumbnail={image.thumbnails?.xs}
									/>
								)
							})}
					</div>
				</PhotosetStepBodyContainer>
				<PageFooter
					endSlot={
						<ButtonLegacy
							disabled={isFetching}
							onClick={onSendToImagenAi}
							type='button'
							variant='primarySuccess'
						>
							{t('common:done')}
						</ButtonLegacy>
					}
					startSlot={
						<div className='flex items-center'>
							<p>
								{t('common:x_images_selected', {
									count: selectedImages.length
								})}
							</p>
							{selectedImages.length > 0 && (
								<ButtonLegacy
									onClick={unselectAll}
									size={'sm'}
									type='button'
									variant={'tertiary'}
								>
									{t('common:unselect_all')}
								</ButtonLegacy>
							)}
						</div>
					}
				/>
			</PhotosetStepWrapper>
			{currentDetailImageIndex !== null && images && (
				<GalleryCurationModal
					currentDetailImageIndex={currentDetailImageIndex}
					onClose={() => setCurrentDetailImageIndex(null)}
					selectableImages={images}
					setCurrentDetailImageIndex={setCurrentDetailImageIndex}
				/>
			)}
		</div>
	)
}

PhotosetCurationStepV1.displayName = 'PhotosetCurationStepV1'
